import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const TABS = ['Profil', 'Atanan Öğrenciler', 'Deneme Dersleri', 'Aktif Dersler', 'Hak Ediş', 'Ödeme Geçmişi'];

interface Teacher {
  id: string; ad_soyad: string; telefon: string | null; email: string | null;
  branslar: string[] | null; sinif_seviyeleri: string[] | null;
  online_ders: boolean; yuz_yuze_ders: boolean;
  musaitlik_notu: string | null; varsayilan_ders_ucreti: number | null;
  hakedis_orani: number; aktif: boolean; aciklama: string | null;
}

interface Student {
  id: string; ogrenci_adi: string; sinif: string | null;
  branslar: string[] | null; aktif: boolean;
}

interface TrialLesson {
  id: string; planlanan_tarih: string | null; planlanan_saat: string | null;
  brans: string | null; durum: string | null; sonuc: string | null;
  ogrenci?: { ogrenci_adi: string };
}

interface ActiveLesson {
  id: string; ders_tarihi: string; brans: string | null;
  islenen_konu: string | null; ders_durumu: string | null; ders_suresi: number | null;
  ogrenci?: { ogrenci_adi: string };
}

interface Payment {
  id: string; tutar: number; dekont_tarihi: string;
  ogretmen_payi: number | null; hakedis_orani: number | null; odeme_durumu: string | null;
  ogrenci?: { ogrenci_adi: string };
}

interface HakEdisOdeme {
  id: string; odeme_tarihi: string; odeme_tutari: number; aciklama: string | null;
}

const statusBadge: Record<string, string> = {
  Yapıldı: 'bg-green-100 text-green-700',
  Planlandı: 'bg-blue-100 text-blue-700',
  Ertelendi: 'bg-yellow-100 text-yellow-700',
  İptal: 'bg-red-100 text-red-700',
  'Öğrenci Katılmadı': 'bg-orange-100 text-orange-700',
  Onaylandı: 'bg-green-100 text-green-700',
  'Dekont Bekleniyor': 'bg-yellow-100 text-yellow-700',
  'Aktif Derse Döndü': 'bg-green-100 text-green-700',
  Kararsız: 'bg-yellow-100 text-yellow-700',
  'Devam Etmeyecek': 'bg-red-100 text-red-700',
};

export default function OgretmenDetayPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [trials, setTrials] = useState<TrialLesson[]>([]);
  const [lessons, setLessons] = useState<ActiveLesson[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [hakEdisOdemeleri, setHakEdisOdemeleri] = useState<HakEdisOdeme[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState<{ email: string; yeni_sifre: string } | null>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleResetPassword = async () => {
    if (!id || !teacher) return;
    if (!window.confirm(`${teacher.ad_soyad} için yeni bir şifre oluşturulacak. Eski şifre geçersiz olacak. Devam edilsin mi?`)) return;
    setResetLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) { showToast('Oturum geçersiz.'); setResetLoading(false); return; }
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/reset-teacher-password`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` }, body: JSON.stringify({ ogretmen_id: id }) }
      );
      const result = await response.json();
      if (!response.ok) { showToast(result.error || 'Hata oluştu.'); } else {
        setResetResult({ email: result.email, yeni_sifre: result.yeni_sifre });
        fetchAll();
      }
    } catch (err) { showToast(err instanceof Error ? err.message : 'Beklenmeyen hata'); }
    finally { setResetLoading(false); }
  };

  const fetchAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const [teacherRes, studentsRes, trialsRes, lessonsRes, paymentsRes, hakRes] = await Promise.all([
      supabase.from('egtpanel_ogretmenler').select('*').eq('id', id).maybeSingle(),
      supabase.from('egtpanel_ogrenciler').select('id, ogrenci_adi, sinif, branslar, aktif').eq('atanan_ogretmen_id', id),
      supabase.from('egtpanel_deneme_dersleri').select('id, planlanan_tarih, planlanan_saat, brans, durum, sonuc, ogrenci:egtpanel_ogrenciler(ogrenci_adi)').eq('ogretmen_id', id).order('planlanan_tarih', { ascending: false }),
      supabase.from('egtpanel_aktif_dersler').select('id, ders_tarihi, brans, islenen_konu, ders_durumu, ders_suresi, ogrenci:egtpanel_ogrenciler(ogrenci_adi)').eq('ogretmen_id', id).order('ders_tarihi', { ascending: false }),
      supabase.from('egtpanel_odemeler').select('id, tutar, dekont_tarihi, ogretmen_payi, hakedis_orani, odeme_durumu, ogrenci:egtpanel_ogrenciler(ogrenci_adi)').eq('ogretmen_id', id).order('dekont_tarihi', { ascending: false }),
      supabase.from('egtpanel_hak_edis_odemeleri').select('*').eq('ogretmen_id', id).order('odeme_tarihi', { ascending: false }),
    ]);

    setTeacher(teacherRes.data);
    setStudents((studentsRes.data || []) as Student[]);
    setTrials((trialsRes.data || []) as unknown as TrialLesson[]);
    setLessons((lessonsRes.data || []) as unknown as ActiveLesson[]);
    setPayments((paymentsRes.data || []) as unknown as Payment[]);
    setHakEdisOdemeleri(hakRes.data || []);
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return (
    <div className="p-6 flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
    </div>
  );

  if (!teacher) return (
    <div className="p-6 flex flex-col items-center justify-center h-64">
      <i className="ri-user-unfollow-line text-5xl text-slate-200 mb-3" />
      <p className="text-slate-400">Öğretmen bulunamadı</p>
      <button onClick={() => navigate('/admin/ogretmenler')} className="mt-4 text-[#1e3a5f] text-sm font-medium cursor-pointer">← Listeye Dön</button>
    </div>
  );

  const approvedPayments = payments.filter((p) => p.odeme_durumu === 'Onaylandı');
  const totalTahsilat = approvedPayments.reduce((a, p) => a + Number(p.tutar), 0);
  const ogretmenHakedisi = approvedPayments.reduce((a, p) => a + Number(p.ogretmen_payi || (Number(p.tutar) * teacher.hakedis_orani / 100)), 0);
  const odenenTutar = hakEdisOdemeleri.reduce((a, p) => a + Number(p.odeme_tutari), 0);
  const bekleyenTutar = Math.max(0, ogretmenHakedisi - odenenTutar);

  // Monthly lesson chart - last 4 months
  const monthlyData = (() => {
    const months: { month: string; dersler: number }[] = [];
    for (let i = 3; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('tr-TR', { month: 'short' });
      const count = lessons.filter((l) => l.ders_tarihi?.startsWith(key)).length;
      months.push({ month: label, dersler: count });
    }
    return months;
  })();

  const handlePaySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const { error } = await supabase.from('egtpanel_hak_edis_odemeleri').insert({
      ogretmen_id: id,
      odeme_tarihi: fd.get('date') as string,
      odeme_tutari: Number(fd.get('amount')),
      aciklama: (fd.get('desc') as string) || null,
      odeme_durumu: 'Ödendi',
    });
    if (!error) {
      showToast('Ödeme kaydedildi!');
      fetchAll();
      (e.target as HTMLFormElement).reset();
    } else { showToast('Hata: Ödeme kaydedilemedi.'); }
  };

  return (
    <div className="p-6 space-y-6">
      {toast && <div className="fixed top-4 right-4 z-50 bg-[#1e3a5f] text-white px-5 py-3 rounded-xl text-sm font-medium">{toast}</div>}

      <div>
        <button onClick={() => navigate('/admin/ogretmenler')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm mb-4 transition-colors cursor-pointer">
          <i className="ri-arrow-left-line" /> Öğretmen Listesine Dön
        </button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[#1e3a5f] text-white text-2xl font-bold">
              {teacher.ad_soyad.charAt(0)}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">{teacher.ad_soyad}</h1>
              <p className="text-slate-500 text-sm">{teacher.branslar?.join(', ') || '-'}</p>
              <p className="text-slate-400 text-xs mt-0.5">{teacher.telefon} {teacher.email && `· ${teacher.email}`}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${teacher.aktif ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{teacher.aktif ? 'Aktif' : 'Pasif'}</span>
            {teacher.online_ders && <span className="bg-cyan-50 text-cyan-700 px-2.5 py-1 rounded-full text-xs font-medium">Online</span>}
            {teacher.yuz_yuze_ders && <span className="bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full text-xs font-medium">Yüz Yüze</span>}
            <button
              onClick={handleResetPassword}
              disabled={resetLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium hover:bg-amber-100 transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
            >
              <i className={`${resetLoading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}`} />
              {resetLoading ? 'Sıfırlanıyor...' : 'Şifre Sıfırla'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Aktif Öğrenci', val: students.filter((s) => s.aktif).length },
          { label: 'Toplam Ders', val: lessons.length },
          { label: 'Toplam Hak Ediş', val: `₺${ogretmenHakedisi.toLocaleString('tr-TR')}` },
          { label: 'Bekleyen', val: `₺${bekleyenTutar.toLocaleString('tr-TR')}` },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500">{s.label}</p>
            <p className="text-xl font-bold text-slate-800 mt-1">{s.val}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100">
        <div className="border-b border-slate-100 overflow-x-auto">
          <div className="flex">
            {TABS.map((tab, i) => (
              <button key={tab} onClick={() => setActiveTab(i)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors cursor-pointer ${activeTab === i ? 'border-[#1e3a5f] text-[#1e3a5f]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                {[
                  { label: 'Ad Soyad', val: teacher.ad_soyad },
                  { label: 'Telefon', val: teacher.telefon || '-' },
                  { label: 'E-posta', val: teacher.email || '-' },
                  { label: 'Branşlar', val: teacher.branslar?.join(', ') || '-' },
                  { label: 'Sınıf Seviyeleri', val: teacher.sinif_seviyeleri?.join(', ') || '-' },
                  { label: 'Online Ders', val: teacher.online_ders ? 'Evet' : 'Hayır' },
                  { label: 'Yüz Yüze', val: teacher.yuz_yuze_ders ? 'Evet' : 'Hayır' },
                  { label: 'Ders Ücreti', val: teacher.varsayilan_ders_ucreti ? `₺${teacher.varsayilan_ders_ucreti}` : '-' },
                  { label: 'Hak Ediş Oranı', val: `%${teacher.hakedis_orani}` },
                  { label: 'Müsaitlik', val: teacher.musaitlik_notu || '-' },
                ].map((r) => (
                  <div key={r.label} className="flex gap-3 py-2 border-b border-slate-50">
                    <span className="text-xs text-slate-400 w-32 shrink-0">{r.label}</span>
                    <span className="text-sm text-slate-700 font-medium">{r.val}</span>
                  </div>
                ))}
                {teacher.aciklama && (
                  <div className="bg-amber-50 rounded-lg p-3 text-sm text-amber-800 mt-2">
                    <i className="ri-sticky-note-line mr-1" />{teacher.aciklama}
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Aylık Ders Sayısı</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="dersler" stroke="#1e3a5f" fill="#1e3a5f20" name="Ders Sayısı" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {activeTab === 1 && (
            <div>
              {students.length === 0 ? (
                <div className="text-center py-12"><i className="ri-user-line text-4xl text-slate-200 block mb-2" /><p className="text-slate-400 text-sm">Atanan öğrenci yok</p></div>
              ) : (
                <div className="space-y-3">
                  {students.map((s) => (
                    <div key={s.id} className="border border-slate-100 rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-slate-800">{s.ogrenci_adi}</div>
                        <div className="text-xs text-slate-400">{s.sinif} {s.branslar?.length ? `· ${s.branslar.join(', ')}` : ''}</div>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${s.aktif ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{s.aktif ? 'Aktif' : 'Pasif'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 2 && (
            <div>
              {trials.length === 0 ? (
                <div className="text-center py-12"><i className="ri-calendar-todo-line text-4xl text-slate-200 block mb-2" /><p className="text-slate-400 text-sm">Deneme dersi yok</p></div>
              ) : (
                <div className="space-y-3">
                  {trials.map((t) => (
                    <div key={t.id} className="border border-slate-100 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-slate-800">
                          {(t.ogrenci as { ogrenci_adi: string } | undefined)?.ogrenci_adi || '—'} — {t.planlanan_tarih || '—'} {t.planlanan_saat?.slice(0, 5) || ''}
                        </span>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge[t.durum || ''] || 'bg-slate-100 text-slate-500'}`}>{t.durum || '—'}</span>
                      </div>
                      <div className="text-sm text-slate-500">{t.brans || '—'} · Sonuç: {t.sonuc || '—'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 3 && (
            <div>
              {lessons.length === 0 ? (
                <div className="text-center py-12"><i className="ri-book-open-line text-4xl text-slate-200 block mb-2" /><p className="text-slate-400 text-sm">Ders kaydı yok</p></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-slate-100 text-xs text-slate-500">
                      <th className="text-left pb-2 pr-4">Tarih</th>
                      <th className="text-left pb-2 pr-4">Öğrenci</th>
                      <th className="text-left pb-2 pr-4">Branş</th>
                      <th className="text-left pb-2 pr-4">Konu</th>
                      <th className="text-left pb-2 pr-4">Süre</th>
                      <th className="text-left pb-2">Durum</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-50">
                      {lessons.map((l) => (
                        <tr key={l.id}>
                          <td className="py-2.5 pr-4 whitespace-nowrap">{l.ders_tarihi}</td>
                          <td className="py-2.5 pr-4 font-medium whitespace-nowrap">{(l.ogrenci as { ogrenci_adi: string } | undefined)?.ogrenci_adi || '—'}</td>
                          <td className="py-2.5 pr-4 text-slate-600 whitespace-nowrap">{l.brans || '—'}</td>
                          <td className="py-2.5 pr-4 text-slate-600 max-w-[150px] truncate">{l.islenen_konu || '—'}</td>
                          <td className="py-2.5 pr-4 text-slate-600 whitespace-nowrap">{l.ders_suresi ? `${l.ders_suresi} dk` : '—'}</td>
                          <td className="py-2.5"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge[l.ders_durumu || ''] || 'bg-slate-100 text-slate-500'}`}>{l.ders_durumu || '—'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 4 && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: 'Toplam Tahsilat', val: `₺${totalTahsilat.toLocaleString('tr-TR')}`, color: 'text-slate-800' },
                  { label: `Hak Ediş (%${teacher.hakedis_orani})`, val: `₺${ogretmenHakedisi.toLocaleString('tr-TR')}`, color: 'text-[#1e3a5f]' },
                  { label: 'Platform Payı', val: `₺${(totalTahsilat - ogretmenHakedisi).toLocaleString('tr-TR')}`, color: 'text-slate-600' },
                  { label: 'Ödenen', val: `₺${odenenTutar.toLocaleString('tr-TR')}`, color: 'text-green-700' },
                  { label: 'Bekleyen', val: `₺${bekleyenTutar.toLocaleString('tr-TR')}`, color: 'text-orange-600' },
                ].map((s) => (
                  <div key={s.label} className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500">{s.label}</p>
                    <p className={`text-lg font-bold mt-1 ${s.color}`}>{s.val}</p>
                  </div>
                ))}
              </div>

              {bekleyenTutar > 0 && (
                <div className="bg-green-50 rounded-xl p-5">
                  <h4 className="text-sm font-semibold text-green-800 mb-3">Ödeme Yap</h4>
                  <form onSubmit={handlePaySubmit} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-green-700 mb-1">Tarih *</label>
                      <input required type="date" name="date" className="w-full px-3 py-2 rounded-lg border border-green-200 text-sm focus:outline-none bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-green-700 mb-1">Tutar (₺) *</label>
                      <input required type="number" name="amount" defaultValue={bekleyenTutar} className="w-full px-3 py-2 rounded-lg border border-green-200 text-sm focus:outline-none bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-green-700 mb-1">Açıklama</label>
                      <input type="text" name="desc" className="w-full px-3 py-2 rounded-lg border border-green-200 text-sm focus:outline-none bg-white" />
                    </div>
                    <div className="sm:col-span-3">
                      <button type="submit" className="px-6 py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors cursor-pointer">
                        <i className="ri-check-line mr-1" /> Ödeme Yapıldı Olarak Kaydet
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Ödeme Geçmişi</h4>
                {hakEdisOdemeleri.length === 0 ? (
                  <p className="text-slate-400 text-sm">Ödeme kaydı yok</p>
                ) : hakEdisOdemeleri.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-2.5 border-b border-slate-50">
                    <div>
                      <span className="text-sm font-medium text-slate-700">{p.odeme_tarihi}</span>
                      {p.aciklama && <span className="text-xs text-slate-400 ml-2">{p.aciklama}</span>}
                    </div>
                    <span className="text-sm font-semibold text-green-700">₺{Number(p.odeme_tutari).toLocaleString('tr-TR')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 5 && (
            <div>
              {payments.length === 0 ? (
                <div className="text-center py-12"><i className="ri-money-dollar-circle-line text-4xl text-slate-200 block mb-2" /><p className="text-slate-400 text-sm">Ödeme kaydı yok</p></div>
              ) : (
                <div className="space-y-3">
                  {payments.map((p) => (
                    <div key={p.id} className="border border-slate-100 rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-slate-800">{(p.ogrenci as { ogrenci_adi: string } | undefined)?.ogrenci_adi || '—'} — ₺{Number(p.tutar).toLocaleString('tr-TR')}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{p.dekont_tarihi} · Öğretmen payı: ₺{Number(p.ogretmen_payi || 0).toLocaleString('tr-TR')}</div>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge[p.odeme_durumu || ''] || 'bg-slate-100 text-slate-500'}`}>{p.odeme_durumu || '—'}</span>
                    </div>
                  ))}
                  <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-600">Toplam Onaylı Tahsilat</span>
                    <span className="text-lg font-bold text-slate-800">₺{totalTahsilat.toLocaleString('tr-TR')}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reset Password Result Modal */}
      {resetResult && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-800">Şifre Sıfırlandı</h2>
              <button onClick={() => setResetResult(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                <div className="flex items-center gap-2 text-green-700 text-sm font-semibold">
                  <i className="ri-checkbox-circle-line" />
                  Yeni şifre oluşturuldu!
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium mb-0.5">Öğretmen</p>
                <p className="text-sm font-semibold text-slate-800">{teacher?.ad_soyad}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium mb-0.5">E-posta</p>
                <p className="text-sm font-mono text-slate-700 break-all">{resetResult.email}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium mb-0.5">Yeni Şifre</p>
                <p className="text-sm font-mono font-semibold text-slate-800">{resetResult.yeni_sifre}</p>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`E-posta: ${resetResult.email}\nŞifre: ${resetResult.yeni_sifre}`);
                  showToast('Yeni şifre bilgisi kopyalandı!');
                }}
                className="w-full py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] transition-colors cursor-pointer"
              >
                <i className="ri-clipboard-line mr-1" /> Kopyala
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}